import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { FormInstance } from '@arco-design/web-react/es/Form';

import { COMPUTE_FIELD_KEYS, ComputeBlock, resolveComputePaths } from '@/delivery-type-plugin/capabilities/compute';
import { SharedMemoryResourceBlock } from '@/delivery-type-plugin/capabilities/shared-memory/SharedMemoryResourceBlock';
import { SystemDiskSizeBlock } from '@/delivery-type-plugin/capabilities/system-disk-size';
import { useOptionalResourceFormContext } from '@/delivery-type-plugin/platform/resource-form';

import cardStyles from '../group-spec-card/group-spec-card.module.less';
import type { MaasNodeGroupsPricingOptions } from '../types';

const EMBEDDED_SYSTEM_DISK_SIZE_FIELD = '_systemDiskSize';
const EMBEDDED_SYSTEM_DISK_PRICING_FIELD = '_systemDiskPricing';
/** 节点 JSON 内嵌网卡写入 resource.nics（ComputeBlock 默认 network.nics 仅用于容器实例顶层表单） */
const EMBEDDED_NICS_FIELD = 'resource.nics';

interface MaasGroupResourceSettingProps extends MaasNodeGroupsPricingOptions {
  form: FormInstance;
  parentForm?: FormInstance;
  namespace?: string;
  isEdit?: boolean;
  inferenceSystem?: { architecture?: string };
  minCpuCores?: number;
  minMemoryGi?: number;
  minSharedMemoryGi?: number;
  allowedGpus?: { resourceType?: string; vendorID?: string; deviceID?: string }[];
  embeddedNodeConfig?: boolean;
  showNic?: boolean;
  systemDiskSizeField?: string;
  systemDiskPricingField?: string;
  presetConfigSnapshot?: Record<string, unknown>;
  nicPricingOptions?: Record<string, unknown>;
  requireGpuSoftware?: boolean;
}

/** 节点组资源信息：CPU/内存 → GPU → 系统盘/共享内存 → 网卡（卡片 + 弹窗） */
export function MaasGroupResourceSetting({
  form,
  parentForm,
  namespace,
  isEdit,
  billModelId: billModelIdProp,
  deploymentTypeId: deploymentTypeIdProp,
  paymentTiming: paymentTimingProp,
  skuType: skuTypeProp,
  resourceEditMode: resourceEditModeProp,
  lockActionsWhenFilled,
  minCpuCores,
  minMemoryGi,
  minSharedMemoryGi,
  embeddedNodeConfig,
  showNic,
  systemDiskSizeField,
  systemDiskPricingField,
  presetConfigSnapshot,
  nicPricingOptions,
  requireGpuSoftware = false,
}: MaasGroupResourceSettingProps) {
  const { t } = useTranslation();
  const resourceFormCtx = useOptionalResourceFormContext();
  const billModelId = billModelIdProp ?? resourceFormCtx?.billModelId;
  const deploymentTypeId = deploymentTypeIdProp ?? resourceFormCtx?.deploymentTypeId;
  const paymentTiming = paymentTimingProp ?? resourceFormCtx?.paymentTiming;
  const skuType = skuTypeProp ?? resourceFormCtx?.skuType;
  const resourceEditMode = resourceEditModeProp ?? resourceFormCtx?.resourceEditMode;

  const computePaths = useMemo(
    () => ({
      ...resolveComputePaths('resource'),
      memory: 'resource.memory.request',
    }),
    [],
  );

  const showSystemDisk = embeddedNodeConfig || Boolean(parentForm && systemDiskSizeField && systemDiskPricingField);
  const systemDiskForm = embeddedNodeConfig ? form : parentForm!;
  const systemDiskPaths = useMemo(
    () => ({
      size: embeddedNodeConfig ? EMBEDDED_SYSTEM_DISK_SIZE_FIELD : systemDiskSizeField!,
      pricing: embeddedNodeConfig ? EMBEDDED_SYSTEM_DISK_PRICING_FIELD : systemDiskPricingField!,
    }),
    [embeddedNodeConfig, systemDiskPricingField, systemDiskSizeField],
  );

  const insertBeforeGpu = (
    <>
      {showSystemDisk ? (
        <SystemDiskSizeBlock
          form={systemDiskForm}
          paths={systemDiskPaths}
          options={{
            billModelId,
            deploymentTypeId,
            paymentTiming,
            skuType,
            resourceEditMode,
            resourceCode: 'SystemVolume',
            lockActionsWhenFilled,
            presetConfigSnapshot,
          }}
        />
      ) : null}
      {embeddedNodeConfig ? <SharedMemoryResourceBlock form={form} resourceEditMode={resourceEditMode} minGi={minSharedMemoryGi} /> : null}
    </>
  );

  return (
    <div className={cardStyles.resourceBlock}>
      <div className={cardStyles.resourceSubTitle}>{t('product:resources.info')}</div>
      <ComputeBlock
        form={form}
        paths={computePaths}
        insertBeforeGpu={insertBeforeGpu}
        options={{
          gpuVariant: 'container',
          showBandwidth: false,
          showGpu: true,
          showNic: Boolean(embeddedNodeConfig && showNic),
          nicsField: EMBEDDED_NICS_FIELD,
          parentForm,
          isEdit,
          billModelId,
          deploymentTypeId,
          paymentTiming,
          skuType,
          resourceEditMode,
          lockActionsWhenFilled,
          presetConfigSnapshot,
          nicPricingOptions,
          vendorDeviceWorkloadType: 'ContainerInstance',
          resourceCodes: { cpu: 'vCPU', memory: 'Memory', gpu: 'vGPU' },
          clearGpuOnArchitectureChange: false,
          requireGpuSoftware,
        }}
        linkageProps={{
          namespace,
          ...(minCpuCores && minCpuCores > 0 ? { cpuMin: minCpuCores } : {}),
          ...(minMemoryGi && minMemoryGi > 0 ? { memoryMin: minMemoryGi } : {}),
          ...(minSharedMemoryGi && minSharedMemoryGi > 0 ? { sharedMemoryMin: minSharedMemoryGi } : {}),
        }}
        fieldModes={{
          [COMPUTE_FIELD_KEYS.architecture]: 'editable',
          [COMPUTE_FIELD_KEYS.bandwidth]: 'hidden',
        }}
      />
    </div>
  );
}

/** @deprecated 使用 MaasGroupResourceSetting */
export const MaasGroupComputeSetting = MaasGroupResourceSetting;
